package com.snakesworld.game.domain

import com.snakesworld.game.engine.GameConstants
import com.snakesworld.game.engine.Vector2D
import com.snakesworld.game.network.PelletNetworkState
import kotlin.random.Random

/**
 * Ambient arena food spawning rules and distributed whole-world density maintenance.
 */
object PelletSpawnCalculator {

    fun calculateTargetPelletCount(playerCount: Int): Int {
        val calculated = GameConstants.BASE_PELLET_COUNT + playerCount.coerceAtLeast(1) * GameConstants.PELLETS_PER_PLAYER
        return calculated.coerceAtMost(GameConstants.MAX_PELLET_COUNT)
    }

    fun calculateMissingPelletCount(currentPelletCount: Int, playerCount: Int): Int {
        val target = calculateTargetPelletCount(playerCount)
        if (currentPelletCount >= target) return 0
        return (target - currentPelletCount).coerceAtLeast(0)
    }

    /**
     * Spawns a batch of ambient pellets distributed across the square arena.
     */
    fun generateAmbientPellets(
        count: Int,
        arenaHalfSize: Float = CollisionDetection.maxPelletCenterInsideArena(),
        idPrefix: String = "pellet",
    ): List<PelletNetworkState> {
        val list = ArrayList<PelletNetworkState>(count)
        val palette = GameConstants.PELLET_PALETTE
        // Sample only inside the centers whose squares fit in the arena, then clamp
        // so a caller-supplied range cannot place food past the wall.
        val range = minOf(arenaHalfSize, CollisionDetection.maxPelletCenterInsideArena()).coerceAtLeast(0f)

        for (i in 0 until count) {
            val rawX = (Random.nextFloat() * 2f - 1f) * range
            val rawY = (Random.nextFloat() * 2f - 1f) * range
            val placed = CollisionDetection.clampPelletInsideArena(Vector2D(rawX, rawY))

            val color = palette[Random.nextInt(palette.size)]
            val mass = 1f
            val id = "${idPrefix}_${Random.nextLong(100000, 999999)}"

            list.add(
                PelletNetworkState(
                    id = id,
                    x = (placed.x * 10f).toInt() / 10f,
                    y = (placed.y * 10f).toInt() / 10f,
                    m = mass,
                    c = color,
                    cp = false,
                    bt = false,
                )
            )
        }
        return list
    }
}
